import React, { useState, useEffect, useMemo, ReactNode } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  StyleSheet,
  Dimensions,
  Platform,
  TouchableWithoutFeedback,
  Animated,
  LayoutChangeEvent,
  Pressable,
  ScrollView,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../theme";
import { ThemeColors } from "../theme/types";

// Configure MaterialIcons for web
if (Platform.OS === "web") {
  const iconFont = require("react-native-vector-icons/Fonts/MaterialIcons.ttf");
  const iconFontStyles = `@font-face {
    src: url(${iconFont});
    font-family: MaterialIcons;
  }`;
  // Create stylesheet
  const style = document.createElement("style");
  style.type = "text/css";
  // Modern browsers use textContent instead of styleSheet
  style.appendChild(document.createTextNode(iconFontStyles));
  // Inject stylesheet
  document.head.appendChild(style);
}

export type LHOverlaySize = "xs" | "small" | "medium" | "large";

export interface LHOverlayProps {
  /**
   * Controls whether the overlay is visible
   */
  visible: boolean;
  /**
   * Function to be called when the overlay is dismissed
   */
  onDismiss: () => void;
  /**
   * The title of the overlay
   */
  title?: string;
  /**
   * The description text of the overlay
   */
  description?: string;
  /**
   * The size of the overlay
   */
  size?: LHOverlaySize;
  /**
   * Controls whether the overlay can be closed (true) or must remain open (false)
   */
  close?: boolean;
  /**
   * Children to be rendered inside the overlay
   */
  children?: ReactNode;
  /**
   * Position of the overlay on the screen ('center' or 'top')
   */
  position?: "center" | "top";
  /**
   * Props for the primary button
   */
  primaryButton?: {
    text: string;
    onPress: () => void;
    disabled?: boolean;
  };
  /**
   * Props for the secondary button
   */
  secondaryButton?: {
    text: string;
    onPress: () => void;
    disabled?: boolean;
  };
  /**
   * Props for the auxiliary button
   */
  auxiliaryButton?: {
    text: string;
    onPress: () => void;
    disabled?: boolean;
  };
  /**
   * Props for the tertiary button (typically "Cancel")
   */
  tertiaryButton?: {
    text: string;
    onPress: () => void;
    disabled?: boolean;
  };
  /**
   * Whether to dismiss the overlay when clicking outside of it
   */
  dismissOnClickOutside?: boolean;
  /**
   * Notification to display within the modal
   */
  notification?: {
    type: "info" | "success" | "warning" | "error";
    text: string;
  };
  /**
   * Link with optional icon
   */
  link?: {
    text: string;
    onPress: () => void;
    iconName?: string;
  };
  /**
   * Text to display in the footer
   */
  footerText?: string;
  /**
   * When true, hide the footer section (buttons and footer text)
   */
  hideFooter?: boolean;
  /**
   * Optional style override for the overlay container (the white box)
   */
  containerStyle?: any;
  /**
   * Background color for the overlay container. Defaults to '#F5F9FF'
   */
  backgroundColor?: string;
  /**
   * Horizontal padding to apply to the header (left and right). Defaults to 24.
   */
  headerPaddingHorizontal?: number;
  /**
   * Top padding to apply to the header area. Defaults to 16.
   */
  headerPaddingTop?: number;
  /**
   * When true, hide the divider line between header and body
   */
  hideHeaderDivider?: boolean;
  /**
   * Optional style overrides for the title Text element
   */
  titleStyle?: any;
  /**
   * Custom padding for the body container (overrides default paddingHorizontal and paddingVertical).
   * Can be a number (applies to all sides), or an object: { horizontal?: number, vertical?: number | { top?: number; bottom?: number } }
   */
  bodyPadding?:
    | number
    | {
        horizontal?: number;
        vertical?: number | { top?: number; bottom?: number };
      };
  /**
   * Custom width for the overlay. Can be a string (e.g., '70%', '500px') or number (pixels).
   */
  width?: string | number;
  /**
   * Custom height for the overlay. Can be a string (e.g., '80vh', '600px') or number (pixels).
   */
  height?: string | number;
}

const LHOverlay = ({
  visible,
  onDismiss,
  title,
  description,
  size = "medium",
  children,
  titleStyle,
  primaryButton,
  secondaryButton,
  auxiliaryButton,
  tertiaryButton,
  dismissOnClickOutside = true,
  close = true,
  notification,
  link,
  footerText,
  containerStyle,
  backgroundColor,
  hideFooter = false,
  position = "center",
  headerPaddingHorizontal = 24,
  headerPaddingTop = 16,
  hideHeaderDivider = false,
  bodyPadding,
  width,
  height,
}: LHOverlayProps) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  // Default panel background reproduces the app's existing overlay appearance
  // (see theme/tokens/lightTheme.ts background.elevated) while remaining
  // overridable via the `backgroundColor` prop, unchanged from before.
  const resolvedBackgroundColor = backgroundColor ?? colors.background.elevated;
  const [fadeAnim] = useState(new Animated.Value(0));
  const [overlayWidth, setOverlayWidth] = useState<number>(0);
  const [maxHeight, setMaxHeight] = useState<number>(0);
  const windowHeight = Dimensions.get("window").height;
  const windowWidth = Dimensions.get("window").width;
  const [isAnimating, setIsAnimating] = useState(false);

  // Safe dismiss function with debounce to prevent rapid toggling
  const handleDismiss = React.useCallback(() => {
    if (!isAnimating && visible) {
      setIsAnimating(true);
      onDismiss();

      // Reset animation lock after a delay, with longer delay for web platform
      setTimeout(() => {
        setIsAnimating(false);
      }, Platform.OS === "web" ? 500 : 300);
    }
  }, [isAnimating, visible, onDismiss]);

  useEffect(() => {
    if (visible) {
      // Set max height based on window size only when opening
      setMaxHeight(windowHeight * 0.9);
      setIsAnimating(false);

      // Fade in animation
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 250,
        useNativeDriver: Platform.OS !== "web",
      }).start(() => {
        setIsAnimating(false);
      });
    } else {
      // Fade out animation - don't update state during close
      setIsAnimating(true);
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: Platform.OS !== "web",
      }).start((finished) => {
        if (finished) {
          setIsAnimating(false);
        }
      });
    }
  }, [visible, fadeAnim, windowHeight]);

  const handleLayout = React.useCallback((event: LayoutChangeEvent) => {
    // Only update layout width when overlay is visible to prevent re-renders during close animation
    if (visible) {
      const newWidth = event.nativeEvent.layout.width;
      // Only update if width actually changed to minimize re-renders
      setOverlayWidth(prevWidth => {
        if (newWidth !== prevWidth) {
          return newWidth;
        }
        return prevWidth;
      });
    }
  }, [visible]);

  const getButtonContainerStyles = () => {
    // Count visible buttons
    const buttonCount = [
      primaryButton,
      secondaryButton,
      auxiliaryButton,
      tertiaryButton,
    ].filter(Boolean).length;

    // For narrow widths or multiple buttons, stack them vertically
    if (overlayWidth < 480 || buttonCount > 2) {
      return styles.buttonsVertical;
    }

    // Otherwise place them horizontally
    return styles.buttonsHorizontal;
  };

  const getOverlaySizeStyles = () => {
    const baseSizes = {
      xs: { maxWidth: 400 },
      small: { maxWidth: 560 },
      medium: { maxWidth: 720 },
      large: { maxWidth: 940 },
    };

    // If custom width is provided, use it
    if (width !== undefined) {
      return {
        width,
        maxHeight: height !== undefined ? height : maxHeight,
      };
    }

    // If only custom height is provided
    if (height !== undefined) {
      // For mobile, use full width with margins
      if (windowWidth < 480) {
        return { width: windowWidth - 32, maxHeight: height };
      }
      return { ...baseSizes[size], maxHeight: height };
    }

    // For mobile, use full width with margins
    if (windowWidth < 480) {
      return { width: windowWidth - 32, maxHeight: maxHeight };
    }

    return { ...baseSizes[size], maxHeight: maxHeight };
  };

  const getNotificationStyles = () => {
    switch (notification?.type) {
      case "success":
        return styles.notificationSuccess;
      case "warning":
        return styles.notificationWarning;
      case "error":
        return styles.notificationError;
      case "info":
      default:
        return styles.notificationInfo;
    }
  };

  const renderButtons = () => {
    const buttonContainerStyle = getButtonContainerStyles();
    const needsReverse = buttonContainerStyle === styles.buttonsHorizontal;

    // Order buttons appropriately
    const buttons = [];

    // In horizontal layout, primary comes last (right)
    // In vertical layout, primary comes first (top)
    if (tertiaryButton) {
      buttons.push(
        <TouchableOpacity
          key="tertiary"
          style={[styles.button, styles.tertiaryButton]}
          onPress={tertiaryButton.onPress}
          disabled={tertiaryButton.disabled}
        >
          <Text style={styles.tertiaryButtonText}>{tertiaryButton.text}</Text>
        </TouchableOpacity>
      );
    }

    if (auxiliaryButton) {
      buttons.push(
        <TouchableOpacity
          key="auxiliary"
          style={[styles.button, styles.auxiliaryButton]}
          onPress={auxiliaryButton.onPress}
          disabled={auxiliaryButton.disabled}
        >
          <Text style={styles.auxiliaryButtonText}>{auxiliaryButton.text}</Text>
        </TouchableOpacity>
      );
    }

    if (secondaryButton) {
      buttons.push(
        <TouchableOpacity
          key="secondary"
          style={[styles.button, styles.secondaryButton]}
          onPress={secondaryButton.onPress}
          disabled={secondaryButton.disabled}
        >
          <Text style={styles.secondaryButtonText}>{secondaryButton.text}</Text>
        </TouchableOpacity>
      );
    }

    if (primaryButton) {
      buttons.push(
        <TouchableOpacity
          key="primary"
          style={[styles.button, styles.primaryButton]}
          onPress={primaryButton.onPress}
          disabled={primaryButton.disabled}
        >
          <Text style={styles.primaryButtonText}>{primaryButton.text}</Text>
        </TouchableOpacity>
      );
    }

    // Reverse order for horizontal layout to get primary on right
    return needsReverse ? buttons.reverse() : buttons;
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="none"
      onRequestClose={close ? handleDismiss : undefined}
      supportedOrientations={[
        "portrait",
        "portrait-upside-down",
        "landscape",
        "landscape-left",
        "landscape-right",
      ]}
      presentationStyle="overFullScreen"
    >
      {/* Add a full-screen container with centered content */}
      <View
        style={[
          styles.modalOverlay,
          {
            justifyContent: position === "top" ? "flex-start" : "center",
            paddingTop: position === "top" ? 60 : 20,
          },
        ]}
      >
        {/* Add a semi-transparent background that can be clicked to dismiss */}
        {dismissOnClickOutside && (
          <TouchableWithoutFeedback onPress={handleDismiss}>
            <View style={styles.modalBackground} />
          </TouchableWithoutFeedback>
        )}
        <Animated.View
          style={[
            styles.overlayContainer,
            { backgroundColor: resolvedBackgroundColor },
            containerStyle,
            getOverlaySizeStyles(),
            { opacity: fadeAnim },
          ]}
          onLayout={handleLayout}
        >
          {/* Header */}
          <View style={styles.header}>
            <View
              style={{
                paddingLeft: headerPaddingHorizontal,
                paddingRight: headerPaddingHorizontal,
                flex: 1,
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                paddingTop: headerPaddingTop,
              }}
            >
              <Text style={[styles.title, titleStyle]}>{title}</Text>
              {close && (
                <Pressable
                  style={styles.closeButton}
                  onPress={handleDismiss}
                  accessibilityLabel="Close overlay"
                >
                  <MaterialIcons name="close" size={24} color={colors.text.primary} />
                </Pressable>
              )}
            </View>

            {/* inset bottom border */}
            {!hideHeaderDivider && (
              <View
                style={{
                  position: "absolute",
                  left: headerPaddingHorizontal,
                  right: headerPaddingHorizontal,
                  bottom: 0,
                  height: 1,
                  backgroundColor: colors.border.divider,
                }}
              />
            )}
          </View>
          
          {!hideHeaderDivider && <View style={styles.ashLine} />}

          {/* Body */}
          <ScrollView
            showsVerticalScrollIndicator={true}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={(() => {
              const base: any = [styles.bodyContainer, { flexGrow: 1 }];
              if (bodyPadding !== undefined) {
                if (typeof bodyPadding === 'number') {
                  base.push({ paddingHorizontal: bodyPadding, paddingTop: bodyPadding, paddingBottom: bodyPadding });
                } else {
                  const horizontal = bodyPadding.horizontal ?? 24;
                  let paddingTop = 16;
                  let paddingBottom = 16;
                  if (typeof bodyPadding.vertical === 'number') {
                    paddingTop = bodyPadding.vertical;
                    paddingBottom = bodyPadding.vertical;
                  } else if (typeof bodyPadding.vertical === 'object' && bodyPadding.vertical !== null) {
                    paddingTop = bodyPadding.vertical.top ?? paddingTop;
                    paddingBottom = bodyPadding.vertical.bottom ?? paddingBottom;
                  }
                  base.push({ paddingHorizontal: horizontal, paddingTop, paddingBottom });
                }
              }
              return base;
            })()}
            nestedScrollEnabled={true}
          >
            {/* Description */}
            {description && <Text style={styles.description}>{description}</Text>}

            {/* Notification */}
            {notification && (
              <View style={[styles.notification, getNotificationStyles()]}>
                <MaterialIcons
                  name={
                    notification.type === "error"
                      ? "error"
                      : notification.type === "warning"
                      ? "warning"
                      : notification.type === "success"
                      ? "check-circle"
                      : "info"
                  }
                  size={20}
                  color={
                    notification.type === "error"
                      ? colors.status.error
                      : notification.type === "warning"
                      ? colors.status.warning
                      : notification.type === "success"
                      ? colors.status.success
                      : colors.status.info
                  }
                />
                <Text style={styles.notificationText}>{notification.text}</Text>
              </View>
            )}

            {/* Custom Content */}
            {children && <View style={styles.customContent}>{children}</View>}

            {/* Link */}
            {link && (
              <TouchableOpacity style={styles.link} onPress={link.onPress}>
                {link.iconName && (
                  <MaterialIcons
                    name={link.iconName}
                    size={16}
                    color={colors.brand.primary}
                    style={styles.linkIcon}
                  />
                )}
                <Text style={styles.linkText}>{link.text}</Text>
              </TouchableOpacity>
            )}
          </ScrollView>

          {/* Footer */}
          {!hideFooter && (
            <View style={styles.footer}>
              {footerText && <Text style={styles.footerText}>{footerText}</Text>}

              {/* Buttons */}
              <View style={getButtonContainerStyles()}>{renderButtons()}</View>
            </View>
          )}
        </Animated.View>
      </View>
    </Modal>
  );
};

// Colors-driven style factory. Every color previously hardcoded here now
// resolves from centralized semantic theme tokens (see src/theme). A few
// values shift by a single, visually negligible shade versus the original
// literal (documented in the Phase 1 report) because several distinct blues
// were used interchangeably across the app for the same "interactive/brand"
// role; `colors.brand.primary` is used as the single canonical value.
const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    modalOverlay: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100%",
      padding: 20, // Add some padding around the modal
    },
    modalBackground: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.overlay,
    },
    overlayContainer: {
      // backgroundColor is resolved at render time (see resolvedBackgroundColor)
      borderRadius: (Platform.OS === 'web' ? 'var(--Corner-radius-corner-radius-small, 8px)' : 8) as any,
      borderWidth: 10,
      borderStyle: "solid",
      borderColor: colors.border.default,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
      elevation: 5,
      width: "90%",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      maxHeight: "90%",
      flexShrink: 1,
      // bottom: '10%',
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingVertical: 0,
    },
    ashLine: {
      height: 1,
      backgroundColor: colors.border.divider, // subtle divider line
      marginHorizontal: 24,
      opacity: 1,
    },
    title: {
      fontFamily: Platform.select({ web: "Inter, -apple-system, Roboto, Helvetica, sans-serif", default: "Inter" }),
      fontWeight: "700",
      fontStyle: "normal",
      fontSize: 20,
      lineHeight: 28,
      letterSpacing: 0,
      textAlignVertical: "center",
      color: colors.text.primary,
      ...(Platform.OS === "web" ? { width: 496 } : { flex: 1 }),
      height: 32,
      paddingLeft: 0,
      paddingRight: 24,
      opacity: 1,
    },
    bodyScrollView: {
      // flexGrow: 1,
      maxHeight: 500, // Limit height to 70% of the overlay
      flexShrink: 1,
      flexBasis: "auto",
      minHeight: 0,
    },
    closeButton: {
      // padding: 8,
    },
    bodyContainer: {
      paddingHorizontal: Platform.select({ web: 8, ios: 24, default: 24 }),
      paddingVertical: 16,
    },
    description: {
      fontSize: 16,
      lineHeight: 24,
      color: colors.text.primary,
      marginBottom: 16,
    },
    notification: {
      flexDirection: "row",
      alignItems: "center",
      padding: 12,
      borderRadius: 4,
      marginBottom: 16,
    },
    notificationInfo: {
      backgroundColor: colors.statusBackground.info,
    },
    notificationSuccess: {
      backgroundColor: colors.statusBackground.success,
    },
    notificationWarning: {
      backgroundColor: colors.statusBackground.warning,
    },
    notificationError: {
      backgroundColor: colors.statusBackground.error,
    },
    notificationText: {
      marginLeft: 8,
      fontSize: 14,
      flex: 1,
      color: colors.text.secondary,
    },
    customContent: {
      marginBottom: 16,
    },
    link: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: 16,
    },
    linkIcon: {
      marginRight: 6,
    },
    linkText: {
      color: colors.brand.primary,
      fontSize: 16,
      textDecorationLine: "underline",
    },
    footer: {
      borderTopWidth: 1,
      borderTopColor: colors.border.divider,
      // padding: 16,
    },

    insetBorder: {
      position: 'absolute',
      bottom: 0,
      height: 1,
      backgroundColor: colors.border.default,
    },
    footerText: {
      fontSize: 12,
      color: colors.text.secondary,
      marginBottom: 16,
    },
    buttonsHorizontal: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: 12,
    },
    buttonsVertical: {
      flexDirection: "column-reverse", // Primary on top
      gap: 12,
    },
    button: {
      paddingVertical: 12,
      paddingHorizontal: 24,
      borderRadius: 4,
      alignItems: "center",
      justifyContent: "center",
      minWidth: 100,
    },
    primaryButton: {
      backgroundColor: colors.brand.primary,
      borderRadius: 20,
    },
    secondaryButton: {
      backgroundColor: "transparent",
      borderWidth: 1,
      borderColor: colors.brand.primary,
    },
    auxiliaryButton: {
      backgroundColor: "transparent",
    },
    tertiaryButton: {
      backgroundColor: "transparent",
    },
    primaryButtonText: {
      color: colors.text.inverse,
      fontSize: 16,
      fontWeight: "600",
    },
    secondaryButtonText: {
      color: colors.brand.primary,
      fontSize: 16,
      fontWeight: "600",
    },
    auxiliaryButtonText: {
      color: colors.brand.primary,
      fontSize: 16,
    },
    tertiaryButtonText: {
      color: colors.text.secondary,
      fontSize: 16,
    },
  });

export default LHOverlay;
